import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { useReportEntries } from '@/app/time-tracking/_shared/hooks';
import { formatDuration } from '@/app/time-tracking/_shared/utils';

export default function ExpandedEntries({
  filters,
  groupBy,
  groupId,
  isAdmin = false,
  showProject = false,
  showEmployee = false,
}) {
  const { t } = useTranslation('forms');
  const { data, isLoading } = useReportEntries({
    ...filters,
    groupBy,
    groupId,
  });
  const entries = data?.data || [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spin />
      </div>
    );
  }

  if (!entries.length) {
    return (
      <div className="py-6 text-center text-gray-400">{t('noTimeEntries')}</div>
    );
  }

  return (
    <div className="ml-12 bg-gray-50/80">
      <div className="flex items-center gap-6 border-b border-gray-200 px-6 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        <span className="w-20 shrink-0">{t('date')}</span>
        {showProject && <span className="w-40 shrink-0">{t('project')}</span>}
        {showEmployee && <span className="w-40 shrink-0">{t('employee')}</span>}
        <span className="w-40 shrink-0">{t('task')}</span>
        <span className="w-20 shrink-0 text-right">{t('duration')}</span>
        {isAdmin && <span className="w-8 shrink-0 text-center">$</span>}
        <span className="min-w-0 flex-1">{t('description')}</span>
      </div>
      {entries.map((entry) => (
        <div
          key={entry._id}
          className="flex items-center gap-6 border-b border-gray-100 px-6 py-2.5 text-sm last:border-b-0"
        >
          <span className="w-20 shrink-0 text-gray-500">
            {entry.date
              ? new Date(entry.date).toLocaleDateString('sr-RS', {
                  day: '2-digit',
                  month: '2-digit',
                })
              : '-'}
          </span>
          {showProject && (
            <span className="w-40 shrink-0">
              {entry.project ? (
                <>
                  <span
                    className="mr-1.5 inline-block h-2.5 w-2.5 rounded-full"
                    style={{
                      backgroundColor: entry.project.color || '#1890ff',
                    }}
                  />
                  {entry.project.name}
                </>
              ) : (
                <span className="italic text-gray-400">
                  {entry.autoGenerated ? t('unallocatedTime') : t('noProject')}
                </span>
              )}
            </span>
          )}
          {showEmployee && (
            <span className="w-40 shrink-0">
              {`${entry.employee?.firstName || ''} ${entry.employee?.lastName || ''}`.trim() ||
                '-'}
            </span>
          )}
          <span className="w-40 shrink-0 text-gray-700">
            {entry.taskName || '-'}
          </span>
          <span className="w-20 shrink-0 text-right font-medium">
            {entry.duration ? formatDuration(entry.duration) : '-'}
          </span>
          {isAdmin && (
            <span className="w-8 shrink-0 text-center">
              {entry.billable ? (
                <span className="text-green-600">$</span>
              ) : null}
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-gray-400">
            {entry.description || ''}
          </span>
        </div>
      ))}
    </div>
  );
}
